import { useRemoteSensors } from "@/hooks/useRemoteSensors";
import { SelectChangeEvent } from "@mui/material";
import { crux_sensor_product_type_e } from "@WahooFitness/crux-js-display-cfg";
import { WSMGearCfg } from "@WahooFitness/wsm-native/dist/esm/types/gear_cfg";
import { useCallback, useMemo } from "react";
import useSWR from "swr";

const useBikeGearConfig = (remoteSensorId: number, appToken?: string) => {
  const { getRemoteSensor, saveRemoteSensorConfig, getShifterCfgOptions } =
    useRemoteSensors(appToken);

  const remoteSensor = useMemo(
    () => getRemoteSensor(remoteSensorId),
    [getRemoteSensor, remoteSensorId]
  );

  const gearCfg = useMemo(() => {
    const result = remoteSensor?.cfg?.bike_gear_cfg;
    return result;
  }, [remoteSensor?.cfg?.bike_gear_cfg]);

  const chainringCount = useMemo(() => {
    const result = gearCfg?.front?.cog_tooth_counts?.length ?? 0;
    return result;
  }, [gearCfg]);

  const cassetteCogCount = useMemo(() => {
    const result = gearCfg?.rear?.cog_tooth_counts?.length ?? 0;
    return result;
  }, [gearCfg]);

  const isKICKRBike = useCallback(() => {
    const result = [
      crux_sensor_product_type_e.WAHOO_KICKR_BIKE,
      crux_sensor_product_type_e.WAHOO_KICKR_BIKE_SHIFT,
    ].includes(remoteSensor?.status?.sensor_product_type ?? 255);
    return result;
  }, [remoteSensor?.status?.sensor_product_type]);

  const { data: cassetteOptions } = useSWR(
    !!remoteSensor && ["cassetteOptions", remoteSensor],
    async ([_key, remoteSensor]) => {
      const gearInfo = await getShifterCfgOptions(remoteSensor);
      return gearInfo?.[`${cassetteCogCount}` as keyof WSMGearCfg]?.cassettes;
    }
  );

  const handleBigRingChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      const newBigRing = +event.target.value;
      const newChainrings = [...(gearCfg?.front?.cog_tooth_counts || [])];
      newChainrings[chainringCount === 3 ? 2 : 1] = newBigRing;
      saveRemoteSensorConfig(remoteSensorId, {
        bike_gear_cfg: {
          front: {
            cog_tooth_counts: newChainrings,
          },
        },
      });
    },
    [chainringCount, gearCfg, remoteSensorId, saveRemoteSensorConfig]
  );

  const handleMiddleRingChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      const newMiddleRing = +event.target.value;
      const newChainrings = [...(gearCfg?.front?.cog_tooth_counts || [])];
      newChainrings[1] = newMiddleRing;
      saveRemoteSensorConfig(remoteSensorId, {
        bike_gear_cfg: {
          front: {
            cog_tooth_counts: newChainrings,
          },
        },
      });
    },
    [gearCfg, remoteSensorId, saveRemoteSensorConfig]
  );

  const handleLittleRingChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      const newMiddleRing = +event.target.value;
      const newChainrings = [...(gearCfg?.front?.cog_tooth_counts || [])];
      newChainrings[0] = newMiddleRing;
      saveRemoteSensorConfig(remoteSensorId, {
        bike_gear_cfg: {
          front: {
            cog_tooth_counts: newChainrings,
          },
        },
      });
    },
    [gearCfg, remoteSensorId, saveRemoteSensorConfig]
  );

  const saveRearCassetteGears = useCallback(
    (gears: number[]) => {
      saveRemoteSensorConfig(remoteSensorId, {
        bike_gear_cfg: {
          rear: {
            cog_tooth_counts: gears,
          },
        },
      });
    },
    [remoteSensorId, saveRemoteSensorConfig]
  );

  return {
    getRemoteSensor,
    gearCfg,
    chainringCount,
    handleBigRingChange,
    handleMiddleRingChange,
    handleLittleRingChange,
    saveRearCassetteGears,
    cassetteOptions,
    isKICKRBike,
  };
};

export { useBikeGearConfig };
